import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, LogIn, UserPlus, Users, Building2, Briefcase, Stethoscope, HelpCircle, MessageSquareQuote } from 'lucide-react';

type NavChild = {
  to: string;
  label: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
  /** Starts a new titled section within the dropdown. */
  group?: string;
};

type NavItem = { label: string; to: string } | { label: string; children: NavChild[] };

const navItems: NavItem[] = [
  { label: 'Features', to: '/features' },
  { label: 'How It Works', to: '/how-it-works' },
  {
    label: 'Solutions',
    children: [
      { to: '/use-cases/therapists', label: 'Solo Practices', description: 'Solo practitioners and counselors', icon: Users, group: 'For BH Practices' },
      { to: '/use-cases/clinics', label: 'Small to Mid-Size Practices', description: 'Multi-provider clinical teams', icon: Building2, group: 'For BH Practices' },
      { to: '/use-cases/group-practices', label: 'Large Group Practices', description: 'Multi-location groups and admin oversight', icon: Briefcase, group: 'For BH Practices' },
      { to: '/apcm', label: 'Advanced Primary Care Management (APCM) Services', description: 'Managed Medicare APCM delivery', icon: Stethoscope, group: 'For PCP Clinics' },
    ],
  },
  { label: 'Pricing', to: '/pricing' },
  {
    label: 'Resources',
    children: [
      { to: '/faq', label: 'FAQ', description: 'Common questions, answered', icon: HelpCircle },
      { to: '/testimonials', label: 'Testimonials', description: 'Real stories from clinicians', icon: MessageSquareQuote },
    ],
  },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

const loginLinks = [
  { label: 'Provider Login', href: 'https://app.doctorite.ai', icon: LogIn },
  { label: 'Client Login', href: 'https://my.doctorite.ai', icon: UserPlus },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  const isHome = location.pathname === '/';
  const useTransparent = isHome && !isScrolled;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
    setOpenDropdown(null);
    setOpenMobileSubmenu(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!openDropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openDropdown]);

  const navLinkClass = (active: boolean) =>
    `px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
      useTransparent
        ? active
          ? 'text-white bg-white/10'
          : 'text-white/80 hover:text-white hover:bg-white/10'
        : active
          ? 'text-brand-700 bg-brand-50'
          : 'text-gray-600 hover:text-brand-600 hover:bg-brand-50'
    }`;

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        useTransparent
          ? 'bg-transparent'
          : 'bg-white/90 backdrop-blur-xl shadow-lg shadow-brand-900/5 border-b border-brand-100/50'
      }`}
    >
      <div className="container-max">
        <div className="flex items-center justify-between h-16 sm:h-20 px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" aria-label="Doctorite AI Home">
            <img
              src="/favicon-icon.png"
              alt="Doctorite"
              className="h-10 sm:h-10 w-auto object-contain"
            />
            <span
              className={`text-lg sm:text-xl font-semibold tracking-tight transition-colors ${useTransparent ? 'text-white' : 'text-gray-900'}`}
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Doctorite
            </span>
          </Link>

          {/* Desktop Nav */}
          <div ref={dropdownRef} className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              if ('children' in item) {
                const isOpen = openDropdown === item.label;
                const isActive = item.children.some((c) => location.pathname.startsWith(c.to));
                return (
                  <div key={item.label} className="relative">
                    <button
                      onClick={() => setOpenDropdown(isOpen ? null : item.label)}
                      className={`flex items-center gap-1 ${navLinkClass(isActive)}`}
                      aria-expanded={isOpen}
                      aria-haspopup="true"
                    >
                      {item.label}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 8, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 8, scale: 0.97 }}
                          transition={{ duration: 0.15 }}
                          className="absolute left-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden p-2"
                        >
                          {item.children.map((child, ci) => {
                            const startsGroup =
                              !!child.group && child.group !== item.children[ci - 1]?.group;
                            return (
                              <React.Fragment key={child.to}>
                                {startsGroup && (
                                  <p
                                    className={`px-3 pb-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-400 ${
                                      ci === 0 ? 'pt-1' : 'pt-3 mt-2 border-t border-gray-100'
                                    }`}
                                  >
                                    {child.group}
                                  </p>
                                )}
                                <NavLink
                                  to={child.to}
                                  onClick={() => setOpenDropdown(null)}
                                  className="flex items-start gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-brand-50 transition-colors group"
                                >
                                  {child.icon && (
                                    <div className="w-9 h-9 rounded-lg bg-brand-50 group-hover:bg-brand-100 text-brand-600 flex items-center justify-center shrink-0 transition-colors">
                                      <child.icon className="w-4 h-4" />
                                    </div>
                                  )}
                                  <div className="flex-1">
                                    <p className="font-semibold text-gray-900 group-hover:text-brand-700">{child.label}</p>
                                    {child.description && (
                                      <p className="text-xs text-gray-500 mt-0.5 leading-snug">{child.description}</p>
                                    )}
                                  </div>
                                </NavLink>
                              </React.Fragment>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) => navLinkClass(isActive)}
                >
                  {item.label}
                </NavLink>
              );
            })}
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3">
            <div className="relative">
              <button
                onClick={() => setOpenDropdown(openDropdown === 'login' ? null : 'login')}
                className={navLinkClass(false)}
                aria-expanded={openDropdown === 'login'}
                aria-haspopup="true"
              >
                <span className="inline-flex items-center gap-1.5">
                  <LogIn className="w-4 h-4" />
                  Login
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openDropdown === 'login' ? 'rotate-180' : ''}`} />
                </span>
              </button>
              <AnimatePresence>
                {openDropdown === 'login' && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
                  >
                    {loginLinks.map((link) => (
                      <a
                        key={link.href}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-brand-50 hover:text-brand-600 transition-colors"
                      >
                        <link.icon className="w-4 h-4" />
                        {link.label}
                      </a>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link to="/contact" className="btn-primary !py-2.5">
              Book Free Demo
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${useTransparent ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-brand-600 hover:bg-brand-50'}`}
            aria-label="Toggle menu"
            aria-expanded={isMobileOpen}
          >
            {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-gray-100 max-h-[80vh] overflow-y-auto"
          >
            <div className="px-4 py-4 space-y-1">
              {navItems.map((item) => {
                if ('children' in item) {
                  const open = openMobileSubmenu === item.label;
                  return (
                    <div key={item.label}>
                      <button
                        onClick={() => setOpenMobileSubmenu(open ? null : item.label)}
                        className="w-full flex items-center justify-between px-4 py-3 text-base font-medium text-gray-700 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                      >
                        {item.label}
                        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {open && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="ml-4 border-l-2 border-brand-100"
                          >
                            {item.children.map((child, ci) => {
                              const startsGroup =
                                !!child.group && child.group !== item.children[ci - 1]?.group;
                              return (
                                <React.Fragment key={child.to}>
                                  {startsGroup && (
                                    <p
                                      className={`px-4 pb-1 text-[11px] font-bold uppercase tracking-wider text-gray-400 ${
                                        ci === 0 ? 'pt-2' : 'pt-4'
                                      }`}
                                    >
                                      {child.group}
                                    </p>
                                  )}
                                  <Link
                                    to={child.to}
                                    className="block px-4 py-2.5 text-sm text-gray-600 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                                  >
                                    {child.label}
                                  </Link>
                                </React.Fragment>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      `block px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                        isActive ? 'text-brand-700 bg-brand-50' : 'text-gray-700 hover:text-brand-600 hover:bg-brand-50'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                );
              })}
              <div className="pt-3 border-t border-gray-100 space-y-2">
                {loginLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-gray-600 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                  >
                    <link.icon className="w-4 h-4" />
                    {link.label}
                  </a>
                ))}
                <Link
                  to="/contact"
                  className="btn-primary w-full text-center mt-2"
                >
                  Book Free Demo
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
